import React, { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Play, X } from "lucide-react";

/* ---------- Section title with the hand-drawn green underline ---------- */
export function SectionTitle({ children }) {
  return (
    <div className="inline-block mb-10">
      <h2 className="font-comfortaa text-[26px] md:text-3xl text-white font-normal">{children}</h2>
      <svg
        viewBox="0 0 200 8"
        className="w-full h-2 mt-1"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M2 5 C 50 2, 150 7, 198 4"
          stroke="#8fae4f"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

/* ---------- Helpers ---------- */
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
  );
  return m ? m[1] : null;
}

/* ---------- Video card (with silent hover preview) ---------- */
export function VideoCard({ video, vertical = false, onPlay }) {
  const [hovered, setHovered] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const hoverTimer = useRef(null);
  const canPreview = video.videoUrl && !getYouTubeId(video.videoUrl);

  const handleEnter = () => {
    if (!canPreview) return;
    hoverTimer.current = setTimeout(() => setHovered(true), 250);
  };

  const handleLeave = () => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
    setPreviewReady(false);
  };

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  return (
    <div
      className="group cursor-pointer"
      onClick={() => onPlay(video)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-cursor="play"
      data-testid={`video-card-${video.id}`}
    >
      <div
        className={`relative overflow-hidden rounded-xl bg-black ring-1 ring-white/10 card-premium ${
          vertical ? "aspect-[9/16]" : "aspect-video"
        }`}
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.05]"
          style={{ transition: "transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)" }}
        />
        {hovered && canPreview && (
          <video
            src={video.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setPreviewReady(true)}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: previewReady ? 1 : 0, transition: "opacity 0.45s ease" }}
            data-testid={`video-preview-${video.id}`}
          />
        )}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-black/10 group-hover:from-black/60 flex items-center justify-center"
          style={{ transition: "background 0.4s ease" }}
        >
          <span
            className={`flex items-center justify-center rounded-full border border-white/70 bg-white/10 backdrop-blur-sm group-hover:bg-white/25 group-hover:border-white ${
              vertical ? "w-14 h-14" : "w-16 h-16"
            }`}
            style={{ transition: "background-color 0.35s ease, border-color 0.35s ease" }}
          >
            <Play
              size={vertical ? 20 : 24}
              className="text-white ml-0.5"
              fill="white"
              strokeWidth={0}
            />
          </span>
        </div>
      </div>
      <p className="font-comfortaa text-[13px] md:text-sm text-white/95 mt-3.5 group-hover:text-white" style={{ transition: "color 0.3s ease" }}>
        {video.title}
      </p>
    </div>
  );
}

/* ---------- Fullscreen lightbox player (supports mp4 + YouTube) ---------- */
export function VideoLightbox({ video, onClose }) {
  const escHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escHandler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", escHandler);
      document.body.style.overflow = "";
    };
  }, [escHandler]);

  if (!video) return null;
  const ytId = getYouTubeId(video.videoUrl);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 animate-fadein"
      onClick={onClose}
      data-testid="video-lightbox"
    >
      <button
        className="absolute top-5 right-5 text-white/80 hover:text-white z-10"
        onClick={onClose}
        aria-label="Close video"
        data-testid="lightbox-close"
        style={{ transition: "color 0.25s ease" }}
      >
        <X size={32} strokeWidth={1.5} />
      </button>
      <div
        className="w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {ytId ? (
          <div className="aspect-video w-full">
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              title={video.title}
              className="w-full h-full rounded-md"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <video
            src={video.videoUrl}
            controls
            autoPlay
            playsInline
            className="w-full max-h-[80vh] rounded-md bg-black"
            data-testid="lightbox-video"
          />
        )}
        <p className="font-comfortaa text-white/90 text-sm mt-4">{video.title}</p>
      </div>
    </div>,
    document.body
  );
}
